




















































/* eslint-disable max-lines-per-function */
import {
  computed,
  defineComponent,
  ref,
  useRoute,
  useRouter,
  watch,
} from '@nuxtjs/composition-api';
import { BaseImage, BaseModal, UCIcon } from '@shared/ui';
import { Logger } from '@vue-storefront/core';

import AMXButton from '~/components/AMXButton.vue';
import BikeSelector from '~/components/BikeSelector/BikeSelector.vue';
import useBikeFinderModal, { IBikeFinderMake } from '~/composables/useBikeFinderModal';
import useBikeVehicleFacets from '~/composables/useBikeVehicleFacets';
import {
  getBikeRoute,
  IBikeSelection,
  IBikeSelectorChange,
  IBikeSelectorOption,
  isCompleteBikeSelection,
} from '~/helpers/bikeSelection';

type ResolveStatus = 'loading' | 'ready' | 'error';

/**
 * Opened by clicking a make card. Separate from the My Garage sidebar on
 * purpose: it only navigates to a bike page, never saves to the garage.
 */
export default defineComponent({
  name: 'BikeFinderModal',
  components: {
    AMXButton,
    BaseImage,
    BaseModal,
    BikeSelector,
    UCIcon,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { isOpen, make, close } = useBikeFinderModal();
    const { resolveMake } = useBikeVehicleFacets('bike-finder');

    const status = ref<ResolveStatus>('loading');
    const resolvedMake = ref<IBikeSelectorOption | null>(null);
    const selection = ref<IBikeSelection>({});
    const isComplete = computed(() => isCompleteBikeSelection(selection.value));

    // Storyblok make titles can differ from the vehicle index, so resolve the
    // facet by slug and query models with its title.
    const isStale = (target: IBikeFinderMake) => !isOpen.value || make.value?.slug !== target.slug;

    const resolve = async (target: IBikeFinderMake) => {
      status.value = 'loading';
      resolvedMake.value = null;
      selection.value = {};
      try {
        const facet = await resolveMake(target.slug);
        if (isStale(target)) return;
        if (!facet) throw new Error(`Unknown bike make: ${target.slug}`);
        resolvedMake.value = { title: facet.title, slug: target.slug };
        selection.value = { make: resolvedMake.value };
        status.value = 'ready';
      } catch (error) {
        if (isStale(target)) return;
        Logger.error('BikeFinderModal: failed to resolve make', error);
        status.value = 'error';
      }
    };

    watch(
      isOpen,
      (open) => {
        if (open && make.value) resolve(make.value);
      },
      { immediate: true },
    );

    // Any navigation (including back/forward) dismisses the popup.
    watch(
      () => route.value.fullPath,
      () => close(),
    );

    const onChange = ({ selection: next }: IBikeSelectorChange) => {
      selection.value = next;
    };

    const goTo = (route: string) => {
      close();
      router.push(route);
    };

    const onSubmit = () => {
      if (isComplete.value) goTo(getBikeRoute(selection.value));
    };

    const onViewAll = () => {
      if (make.value)
        goTo(getBikeRoute({ make: { title: make.value.title, slug: make.value.slug } }));
    };

    return {
      isOpen,
      make,
      close,
      status,
      resolvedMake,
      isComplete,
      onChange,
      onSubmit,
      onViewAll,
    };
  },
});
